import {
  IBackendPosProvider,
  IBackendPosProviderStream,
  IBackendDeliveryModuleConfig,
} from "../../types/backend";
import { IPosProvider } from "../../types/pos";
import { produce } from "immer";
import {
  convertMenuConfigToBackend,
  convertMenuConfigToFrontend,
  convertPrepTimeToBackend,
  convertPrepTimeToFrontend,
} from "./menu-converter";

export function convertPosProviderToFrontend(
  backendPosProvider: IBackendPosProvider,
): IPosProvider | null {
  if (backendPosProvider == null) {
    return null;
  }

  return produce(backendPosProvider as unknown as IPosProvider, (draft) => {
    if (draft?.orderingModuleConfig?.deliveryModuleConfig) {
      // convert delivery fee to dollars
      draft.orderingModuleConfig.deliveryModuleConfig.deliveryFee =
        convertCurrencyToFrontend(
          draft.orderingModuleConfig.deliveryModuleConfig.deliveryFee,
        ) || 0;

      // convert waiveDeliveryMinimum to dollars
      draft.orderingModuleConfig.deliveryModuleConfig.waiveDeliveryMinimum =
        convertCurrencyToFrontend(
          draft.orderingModuleConfig.deliveryModuleConfig.waiveDeliveryMinimum,
        );

      // convert minTotalThreshold to dollars
      draft.orderingModuleConfig.deliveryModuleConfig.minTotalThreshold =
        convertCurrencyToFrontend(
          draft.orderingModuleConfig.deliveryModuleConfig.minTotalThreshold,
        );

      // convert maxTotalThreshold to dollars
      draft.orderingModuleConfig.deliveryModuleConfig.maxTotalThreshold =
        convertCurrencyToFrontend(
          draft.orderingModuleConfig.deliveryModuleConfig.maxTotalThreshold,
        );

      // convert coordinates from object format
      const { latitude, longitude } =
        backendPosProvider.orderingModuleConfig.deliveryModuleConfig
          .coordinates;
      if (latitude != null && longitude != null) {
        draft.orderingModuleConfig.deliveryModuleConfig.latitude = latitude;
        draft.orderingModuleConfig.deliveryModuleConfig.longitude = longitude;
        if ("coordinates" in draft.orderingModuleConfig.deliveryModuleConfig) {
          delete draft.orderingModuleConfig.deliveryModuleConfig.coordinates;
        }
      }
    }

    if (draft?.orderingModuleConfig?.pickupModuleConfig) {
      // convert minTotalThreshold to dollars
      draft.orderingModuleConfig.pickupModuleConfig.minTotalThreshold =
        convertCurrencyToFrontend(
          draft.orderingModuleConfig.pickupModuleConfig.minTotalThreshold,
        );

      // convert maxTotalThreshold to dollars
      draft.orderingModuleConfig.pickupModuleConfig.maxTotalThreshold =
        convertCurrencyToFrontend(
          draft.orderingModuleConfig.pickupModuleConfig.maxTotalThreshold,
        );
    }

    if (draft?.orderingModuleConfig) {
      // Convert prepTime
      draft.orderingModuleConfig.prepTime = convertPrepTimeToFrontend(
        backendPosProvider.orderingModuleConfig.prepTime ?? "",
      );

      // Convert min lead time
      if (backendPosProvider.orderingModuleConfig.minLeadTime) {
        draft.orderingModuleConfig.minLeadTime = convertPrepTimeToFrontend(
          backendPosProvider.orderingModuleConfig.minLeadTime,
        );
      }

      // convert min total threshold
      if (backendPosProvider.orderingModuleConfig.minTotalThreshold) {
        draft.orderingModuleConfig.minTotalThreshold =
          convertCurrencyToFrontend(
            backendPosProvider.orderingModuleConfig.minTotalThreshold,
          );
      }

      // convert max total threshold
      if (backendPosProvider.orderingModuleConfig.maxTotalThreshold) {
        draft.orderingModuleConfig.maxTotalThreshold =
          convertCurrencyToFrontend(
            backendPosProvider.orderingModuleConfig.maxTotalThreshold,
          );
      }

      // Convert taxRate if it exists
      if (
        "taxRate" in backendPosProvider.orderingModuleConfig &&
        backendPosProvider.orderingModuleConfig.taxRate !== undefined
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- grace - before eslint enforced
        (draft.orderingModuleConfig as any).taxRate = convertTaxRateToFrontend(
          backendPosProvider.orderingModuleConfig.taxRate as number,
        );
      }
    }

    // Apply menu config conversions
    if (draft?.menuModuleConfig && backendPosProvider.menuModuleConfig) {
      Object.assign(
        draft.menuModuleConfig,
        convertMenuConfigToFrontend(backendPosProvider.menuModuleConfig),
      );
    }
  });
}

export function convertTaxRateToFrontend(taxRate: number): number {
  return taxRate * 100.0;
}

export function convertTaxRateToBackend(taxRate: number): number {
  return taxRate / 100.0;
}

function convertCurrencyToFrontend(
  currencyCents: number | null | undefined,
): number | null | undefined {
  if (currencyCents === undefined) {
    return undefined;
  }
  if (currencyCents === null) {
    return null;
  }
  // to dollars
  return parseFloat((currencyCents / 100.0).toFixed(2));
}

function convertCurrencyToBackend(
  currencyDollars: number | null | undefined,
): number | null | undefined {
  if (currencyDollars === undefined) {
    return undefined;
  }
  if (currencyDollars === null) {
    return null;
  }
  // to cents
  return Math.round(currencyDollars * 100);
}

export function convertPosProviderToBackend(
  posProvider: IPosProvider | null,
): IBackendPosProvider | null {
  if (!posProvider) return null;

  return produce(posProvider as unknown as IBackendPosProvider, (draft) => {
    if (draft?.orderingModuleConfig?.deliveryModuleConfig) {
      // convert delivery fee to cents
      draft.orderingModuleConfig.deliveryModuleConfig.deliveryFee =
        convertCurrencyToBackend(
          draft.orderingModuleConfig.deliveryModuleConfig.deliveryFee,
        ) || 0;

      // convert waiveDeliveryMinimum to cents
      draft.orderingModuleConfig.deliveryModuleConfig.waiveDeliveryMinimum =
        convertCurrencyToBackend(
          draft.orderingModuleConfig.deliveryModuleConfig.waiveDeliveryMinimum,
        ) ?? undefined;

      // convert minTotalThreshold to cents
      draft.orderingModuleConfig.deliveryModuleConfig.minTotalThreshold =
        convertCurrencyToBackend(
          draft.orderingModuleConfig.deliveryModuleConfig.minTotalThreshold,
        ) ?? undefined;

      // convert maxTotalThreshold to cents
      draft.orderingModuleConfig.deliveryModuleConfig.maxTotalThreshold =
        convertCurrencyToBackend(
          draft.orderingModuleConfig.deliveryModuleConfig.maxTotalThreshold,
        ) ?? undefined;

      // Convert coordinates to object format
      const latitude =
        posProvider.orderingModuleConfig.deliveryModuleConfig.latitude;
      const longitude =
        posProvider.orderingModuleConfig.deliveryModuleConfig.longitude;
      if (latitude != null && longitude != null) {
        (
          draft.orderingModuleConfig
            .deliveryModuleConfig as IBackendDeliveryModuleConfig
        ).coordinates = {
          latitude,
          longitude,
        };
        if ("latitude" in draft.orderingModuleConfig.deliveryModuleConfig) {
          delete draft.orderingModuleConfig.deliveryModuleConfig.latitude;
        }
        if ("longitude" in draft.orderingModuleConfig.deliveryModuleConfig) {
          delete draft.orderingModuleConfig.deliveryModuleConfig.longitude;
        }
      }
    }

    if (draft?.orderingModuleConfig?.pickupModuleConfig) {
      // convert minTotalThreshold to cents
      draft.orderingModuleConfig.pickupModuleConfig.minTotalThreshold =
        convertCurrencyToBackend(
          draft.orderingModuleConfig.pickupModuleConfig.minTotalThreshold,
        ) ?? undefined;

      // convert maxTotalThreshold to cents
      draft.orderingModuleConfig.pickupModuleConfig.maxTotalThreshold =
        convertCurrencyToBackend(
          draft.orderingModuleConfig.pickupModuleConfig.maxTotalThreshold,
        ) ?? undefined;
    }

    // Convert prepTime to ISO duration format
    if (draft?.orderingModuleConfig) {
      draft.orderingModuleConfig.prepTime = convertPrepTimeToBackend(
        // @ts-expect-error - this field is required no matter what
        posProvider.orderingModuleConfig.prepTime,
      );

      if (posProvider.orderingModuleConfig.minLeadTime) {
        draft.orderingModuleConfig.minLeadTime = convertPrepTimeToBackend(
          posProvider.orderingModuleConfig.minLeadTime,
        );
      }

      if (posProvider.orderingModuleConfig.minTotalThreshold) {
        draft.orderingModuleConfig.minTotalThreshold =
          convertCurrencyToBackend(
            posProvider.orderingModuleConfig.minTotalThreshold,
          ) ?? undefined;
      }

      if (posProvider.orderingModuleConfig.maxTotalThreshold) {
        draft.orderingModuleConfig.maxTotalThreshold =
          convertCurrencyToBackend(
            posProvider.orderingModuleConfig.maxTotalThreshold,
          ) ?? undefined;
      }

      // Convert tax rate to decimal format if it exists
      if (
        "taxRate" in posProvider.orderingModuleConfig &&
        posProvider.orderingModuleConfig.taxRate !== undefined
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- grace - before eslint enforced
        (draft.orderingModuleConfig as any).taxRate = convertTaxRateToBackend(
          posProvider.orderingModuleConfig.taxRate as number,
        );
      }
    }

    // Convert menu config
    if (draft?.menuModuleConfig && posProvider.menuModuleConfig) {
      Object.assign(
        draft.menuModuleConfig,
        convertMenuConfigToBackend(posProvider.menuModuleConfig),
      );
    }

    // streamUrl is only for toast subprovider
    if (posProvider.provider === "stream") {
      const streamProvider = draft as IBackendPosProviderStream;
      if (streamProvider.subprovider !== "toast" && streamProvider.url) {
        streamProvider.url = undefined;
      }
    }
  });
}
